import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, FieldTextInput, InlineTextButton } from '../../../components';
import { createResourceLocatorString } from '../../../util/routes';
import routeConfiguration from '../../../routing/routeConfiguration';
import { useMyContextFunctions } from '../../../context/ContextFunctions';
import { SIGNUP } from '../../../util/enums';

import css from './LoginForm.module.css';

const LoginFormComponent = props => {
  const history = useHistory();
  const { onSetAuthType, onOpenAuthModal } = useMyContextFunctions();

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          contentClassName,
          titleClassName,
          formId,
          handleSubmit,
          inProgress,
          isModal,
          intl,
          invalid,
          renderLoginOrSignupError,
        } = fieldRenderProps;

      // email
      const emailLabel = intl.formatMessage({
        id: 'LoginForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'LoginForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'LoginForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'LoginForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      // password
      const passwordLabel = intl.formatMessage({
        id: 'LoginForm.passwordLabel',
      });
      const passwordPlaceholder = intl.formatMessage({
        id: 'LoginForm.passwordPlaceholder',
      });
      const passwordRequiredMessage = intl.formatMessage({
        id: 'LoginForm.passwordRequired',
      });
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);

      // The modal keeps its own look; the log-in page (AuthLayout) gives the heading, the errors,
      // the social logins and the "Don't have an account?" line itself.
      const classes = classNames(rootClassName || (isModal ? css.root : css.pageRoot), className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div
            className={classNames(isModal ? css.loginContent : css.pageContent, contentClassName)}
          >
            {isModal ? (
              <h2 className={classNames(css.title, titleClassName)}>
                <FormattedMessage id="LoginForm.loginTitle" />
              </h2>
            ) : null}
            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />
            <div className={isModal ? css.password : null}>
              <div className={css.passwordLabelRow}>
                <label
                  className={css.passwordLabel}
                  htmlFor={formId ? `${formId}.password` : 'password'}
                >
                  {passwordLabel}
                </label>
                <InlineTextButton
                  className={css.resetPasswordLink}
                  type="button"
                  onClick={() => {
                    if (isModal) {
                      onOpenAuthModal(false);
                    }

                    history.push(
                      createResourceLocatorString('PasswordRecoveryPage', routeConfiguration(), {})
                    );
                  }}
                >
                  <FormattedMessage id="LoginForm.resetPasswordLink" />
                </InlineTextButton>
              </div>
              <FieldTextInput
                type="password"
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="current-password"
                placeholder={passwordPlaceholder}
                validate={passwordRequired}
              />
            </div>

            {renderLoginOrSignupError ? (
              <div className={css.errorWrapper}>{renderLoginOrSignupError}</div>
            ) : null}
            <div className={isModal ? css.bottomWrapper : css.pageBottom}>
              <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
                <FormattedMessage id="LoginForm.logIn" />
              </PrimaryButton>
            </div>
            {isModal ? (
              <span className={css.helperText}>
                <FormattedMessage
                  id="LoginForm.dontHaveAccount"
                  values={{
                    signupLink: (
                      <InlineTextButton
                        className={css.loginLink}
                        type="button"
                        onClick={() => onSetAuthType(SIGNUP)}
                      >
                        <FormattedMessage id="LoginForm.signupLink" />
                      </InlineTextButton>
                    ),
                  }}
                />
              </span>
            ) : null}
          </div>
        </Form>
      );
    }}
    />
  );
};

LoginFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  contentClassName: null,
  titleClassName: null,
  form: null,
  inProgress: false,
};

const { string, bool } = PropTypes;

LoginFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  contentClassName: string,
  titleClassName: string,
  form: string,
  inProgress: bool,
  intl: intlShape.isRequired,
};

const LoginForm = compose(injectIntl)(LoginFormComponent);
LoginForm.displayName = 'LoginForm';

export default LoginForm;
